import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { tokenManager } from '../utils/tokenManager'

// HTTP link for GraphQL requests
const httpLink = createHttpLink({
  uri: '/v1/api',
  fetch,
})

// Authentication link to add Bearer token to requests
const authLink = setContext(async (_, { headers }) => {
  try {
    // Try to get JWT token first
    const token = await tokenManager.getAccessToken()
    if (token) {
      console.debug('Using JWT token for GraphQL request')
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      }
    }
    
    // Fallback to API key if no JWT token
    const apiKey = import.meta.env.VITE_API_KEY
    if (apiKey) {
      console.debug('Using API key for GraphQL request (JWT not available)')
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${apiKey}`,
        },
      }
    }
    
    // No authentication available
    console.warn('No authentication available for GraphQL request')
    return {
      headers: {
        ...headers,
      },
    }
  } catch (error) {
    console.error('Error setting up authentication for GraphQL:', error)
    
    // Fallback to API key on error
    const apiKey = import.meta.env.VITE_API_KEY
    if (apiKey) {
      console.debug('Falling back to API key due to JWT error')
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${apiKey}`,
        },
      }
    }
    
    return {
      headers: {
        ...headers,
      },
    }
  }
})

// Error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError, operation: _operation, forward: _forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`)
    })
  }

  if (networkError) {
    console.error(`Network error: ${networkError}`)
    
    // Handle 401/403 errors - token might be expired
    if ('statusCode' in networkError && (networkError.statusCode === 401 || networkError.statusCode === 403)) {
      console.log('Authentication error detected, attempting token refresh')
      
      // For now, just redirect to login on auth errors
      // In a production app, you might want to implement proper token refresh with retry logic
      setTimeout(() => {
        window.location.href = '/login'
      }, 100)
    }
  }
})

// Combine all links
const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    },
    query: {
      errorPolicy: 'all'
    }
  }
})

export default apolloClient
